import { c as _c } from "react/compiler-runtime";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { Heart, Sparkles, Store } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { toast } from "sonner";
import { api, type FeedCollection, type Product, type Shop } from "@/api";
import { EmptyState, ErrorState } from "@/components/common/states";
import { IdeaCartModal } from "@/components/feed/idea-cart-modal";
import { BellIcon, BellOffIcon, ChevronDown16Icon, ChevronRight16Icon, FavoriteHeartIcon, XIcon } from "@/components/icons";
import { Price } from "@/components/product/price";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Chip } from "@/components/ui/chip";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@osmix/ui/skeleton";
import { cn } from "@/lib/utils";
import { useCart, useWishlist } from "@/store";
import { ideaLikeId } from "@/store";
import { IdeaCard, IdeaWall } from "./feed";
import { notifyAddedToCart } from "@/lib/cart-toast";
import { orderQty } from "@/lib/min-order";

// Страница «Избранное» 1:1 по Figma: заголовок + подчёркнутые вкладки
// (Товары / Магазины / Идеи, активная — линия #1F1F1F). Данные и логика вишлиста
// сохранены: products.byIds(ids), shops.list(), feed.collections() и стор
// (ids/shopIds, toggleProduct/toggleShop, *LikedOnServer, syncFromServer).
type WishlistTab = "products" | "shops" | "ideas";
export function WishlistPage() {
  const $ = _c(52);
  const {
    t
  } = useTranslation();
  const ids = useWishlist(_temp);
  useWishlist(_temp2);
  const syncFromServer = useWishlist(_temp3);
  let t0;
  if ($[0] !== ids) {
    t0 = ids.join(",");
    $[0] = ids;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = ["products", "byIds", t0];
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== ids) {
    t2 = () => api.products.byIds(ids);
    $[4] = ids;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t1 || $[7] !== t2) {
    t3 = {
      queryKey: t1,
      queryFn: t2
    };
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const products = useQuery(t3);
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      queryKey: ["shops", "list"],
      queryFn: _temp4
    };
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const shops = useQuery(t4);
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      queryKey: ["feed", "collections"],
      queryFn: _temp5
    };
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const collections = useQuery(t5);
  const [tab, setTab] = useState("products");
  let t6;
  let t7;
  if ($[11] !== syncFromServer) {
    t6 = () => {
      syncFromServer();
    };
    t7 = [syncFromServer];
    $[11] = syncFromServer;
    $[12] = t6;
    $[13] = t7;
  } else {
    t6 = $[12];
    t7 = $[13];
  }
  useEffect(t6, t7);
  let t8;
  if ($[14] !== t) {
    t8 = t("wishlist.tabProducts", {
      defaultValue: "\u0422\u043E\u0432\u0430\u0440\u044B"
    });
    $[14] = t;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t8) {
    t9 = ["products", t8];
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t) {
    t10 = t("wishlist.tabShops", {
      defaultValue: "\u041C\u0430\u0433\u0430\u0437\u0438\u043D\u044B"
    });
    $[18] = t;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t10) {
    t11 = ["shops", t10];
    $[20] = t10;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t) {
    t12 = t("wishlist.tabIdeas", {
      defaultValue: "\u0418\u0434\u0435\u0438"
    });
    $[22] = t;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t12) {
    t13 = ["ideas", t12];
    $[24] = t12;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t11 || $[27] !== t13 || $[28] !== t9) {
    t14 = [t9, t11, t13];
    $[26] = t11;
    $[27] = t13;
    $[28] = t9;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  const tabs = t14;
  let t15;
  if ($[30] !== t) {
    t15 = t("wishlist.title");
    $[30] = t;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== t15) {
    t16 = <h1 className="text-[20px] leading-[23px] font-semibold tracking-[-0.31px] text-[#2E2E2E]">{t15}</h1>;
    $[32] = t15;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== tab || $[35] !== tabs) {
    t17 = <div role="tablist" className="mt-5 flex gap-5">{tabs.map(t18 => {
        const [key, label] = t18;
        return <button key={key} type="button" role="tab" aria-selected={tab === key} onClick={() => setTab(key)} className={cn("relative rounded-[4px] pb-[3.5px] text-[16px] leading-5 text-[#1F1F1F] outline-none focus-visible:ring-2 focus-visible:ring-ds-focus", tab === key ? "font-semibold tracking-[-0.31px]" : "font-normal")}>{label}<span aria-hidden="true" className={cn("absolute inset-x-0 bottom-0 h-[1.5px] rounded-none bg-[#1F1F1F] transition-opacity", tab === key ? "opacity-100" : "opacity-0")} /></button>;
      })}</div>;
    $[34] = tab;
    $[35] = tabs;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== products || $[38] !== tab) {
    t18 = tab === "products" && <ProductsTab query={products} />;
    $[37] = products;
    $[38] = tab;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== shops || $[41] !== tab) {
    t19 = tab === "shops" && <ShopsTab query={shops} />;
    $[40] = shops;
    $[41] = tab;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== collections || $[44] !== tab) {
    t20 = tab === "ideas" && <IdeasTab query={collections} />;
    $[43] = collections;
    $[44] = tab;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== t16 || $[47] !== t17 || $[48] !== t18 || $[49] !== t19 || $[50] !== t20) {
    t21 = <div className="flex flex-col">{t16}{t17}{t18}{t19}{t20}</div>;
    $[46] = t16;
    $[47] = t17;
    $[48] = t18;
    $[49] = t19;
    $[50] = t20;
    $[51] = t21;
  } else {
    t21 = $[51];
  }
  return t21;
}

// Тело вкладки (магазины, идеи, пустые состояния) — в 36px под строкой вкладок
// (Figma: 20px низ строки вкладок + 16px верх ленты); 3,5px уже занимает подчёркивание.
function _temp5() {
  return api.feed.collections();
}
function _temp4() {
  return api.shops.list();
}
function _temp3(s_1) {
  return s_1.syncFromServer;
}
function _temp2(s_0) {
  return s_0.shopIds;
}
function _temp(s) {
  return s.ids;
}
const TAB_BODY = "mt-[32.5px]";

// ── Товары ──────────────────────────────────────────────────────────────────
// Чипы категорий выводим из самих избранных товаров (categoryId + display
// name) — без выдуманных данных; фильтрация по выбранной категории на клиенте.
function ProductsTab({
  query
}: {
  query: UseQueryResult<Product[]>;
}) {
  const {
    t
  } = useTranslation();
  const [category, setCategory] = useState<string | null>(null);
  const items = query.data ?? [];
  const categories = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of items) if (p.categoryId) map.set(p.categoryId, p.category || p.categoryId);
    return [...map].map(([value, label]) => ({
      value,
      label
    }));
  }, [items]);
  if (query.isPending) return <ProductsSkeleton />;
  if (query.error) return <div className={TAB_BODY}>
				<ErrorState onRetry={() => query.refetch()} />
			</div>;
  if (items.length === 0) return <div className={TAB_BODY}>
				<EmptyState icon={Heart} title={t("wishlist.emptyProducts")} hint={t("wishlist.emptyProductsHint")} />
			</div>;
  const shown = category ? items.filter(p_0 => p_0.categoryId === category) : items;

  // Figma: чипы в 30px под строкой вкладок, плитки — в 26px под чипами.
  return <div className={cn("flex flex-col gap-[26px]", categories.length > 0 ? "mt-[26.5px]" : "mt-[32.5px]")}>
			{categories.length > 0 && <div className="flex flex-wrap gap-4">
					<CategoryChip active={category === null} label={t("wishlist.allCategories", {
        defaultValue: "Все"
      })} onClick={() => setCategory(null)} />
					{categories.map(c => <CategoryChip key={c.value} active={category === c.value} label={c.label} onClick={() => setCategory(c.value)} />)}
				</div>}

			{/* Figma (кадр 1200): 6 колонок по 173px, зазор 20px по обеим осям.
       Брейкпоинт в rem (75rem = 1200px): с px Tailwind ставит правило раньше lg. */}
			<div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4 min-[75rem]:grid-cols-6">
				{shown.map(product => <FavoriteProductCard key={product.id} product={product} />)}
			</div>
		</div>;
}

// Чип категории — DS `Chip` sm (h32, 14/18 w500); выбранный — заливка #FA5C2E 5 %
// и обводка #FA5C2E 0,5px, как активный чип в макете.
function CategoryChip(t0) {
  const $ = _c(4);
  const {
    active,
    label,
    onClick
  } = t0;
  let t1;
  if ($[0] !== active || $[1] !== label || $[2] !== onClick) {
    t1 = <Chip selected={active} onClick={onClick}>{label}</Chip>;
    $[0] = active;
    $[1] = label;
    $[2] = onClick;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}

// Минимальная цена по доступным вариантам (сходится с карточкой каталога),
// иначе — базовая цена товара.
function favoritePrice(product: Product): {
  value: number;
  oldValue?: number;
} {
  const variants = product.variants ?? [];
  if (variants.length === 0) return {
    value: product.price,
    oldValue: product.oldPrice
  };
  const inStock = variants.filter(v => v.stock > 0);
  const offered = inStock.length > 0 ? inStock : variants;
  const cheapest = offered.reduce((min, v) => v.price < min.price ? v : min);
  return {
    value: cheapest.price,
    oldValue: cheapest.oldPrice
  };
}
function FavoriteProductCard({
  product
}: {
  product: Product;
}) {
  const {
    t
  } = useTranslation();
  const navigate = useNavigate();
  const toggleProduct = useWishlist(s => s.toggleProduct);
  const setProductLikedOnServer = useWishlist(s_0 => s_0.setProductLikedOnServer);
  const addToCart = useCart(s_1 => s_1.add);
  const soldOut = product.stock <= 0;
  const cover = product.photos[0];
  const price = favoritePrice(product);
  const sizes = product.sizes;
  // Размер, с которым товар положили в избранное (со страницы товара), —
  // подставляем в селект, если он есть среди размеров товара.
  const savedSize = useWishlist(s_2 => s_2.sizes[product.id]);
  // Размер не выбран при добавлении в избранное — и здесь «не выбран»
  // (плейсхолдер «Выбрать размер»), а не первый размер списка (A-28).
  const [size, setSize] = useState(() => savedSize && sizes.includes(savedSize) ? savedSize : sizes.length === 1 ? sizes[0] ?? "" : "");
  // «В корзину» без размера: красная подсказка под кнопкой, как на карточке
  // товара, а не всплывающее уведомление.
  const [sizeHint, setSizeHint] = useState(false);
  const pickSize = (next: string) => {
    setSize(next);
    setSizeHint(false);
  };
  const title = product.brand || product.name;
  const remove = () => {
    const liked = toggleProduct(product.id);
    void setProductLikedOnServer(product.id, liked);
  };
  const onAdd = async () => {
    if (soldOut) return;
    if (sizes.length > 1 && !size) {
      setSizeHint(true);
      return;
    }
    try {
      // Товары с вариантами требуют variantId — резолвим по выбранному размеру.
      const variants = product.variants ?? [];
      const variant = variants.length ? variants.find(v_0 => v_0.size === size && v_0.stock > 0) ?? variants.find(v => v.stock > 0) ?? variants[0] : undefined;
      await addToCart({
        productId: product.id,
        variantId: variant?.id,
        size: variant?.size ?? (size || sizes[0] || ""),
        color: variant?.color ?? product.color,
        qty: orderQty(product)
      });
      notifyAddedToCart(t, () => navigate("/cart"), {
        photo: product.photos[0],
        brand: product.brand,
        name: product.name
      });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("common.error"));
    }
  };

  // Figma «Избранные товары»: фото 173×229 r4; сердце «Избранное на фото» 32
  // (активное, тангело) в 8px от угла, без белого кружка и тени; у закончившегося
  // товара — крестик 24 #333 в 8px от угла. Текст, селект и кнопка отбиты от
  // краёв фото на 6px.
  return <div className="group flex flex-col gap-2">
			<div className="relative aspect-[173/229] overflow-hidden rounded-[4px] bg-osmix-gray-100">
				<Link to={`/product/${product.id}`} className="absolute inset-0 outline-none focus-visible:ring-2 focus-visible:ring-ds-focus">
					{cover ? <img src={cover} alt={product.name} loading="lazy" className={cn("size-full object-cover transition-transform duration-300 group-hover:scale-105", soldOut && "opacity-50")} /> : <div className="flex size-full items-center justify-center px-2 text-center type-small text-osmix-icon">
							{product.name}
						</div>}
				</Link>

				<button type="button" aria-label={t("wishlist.remove")} onClick={remove} className="absolute top-2 right-2 flex size-8 items-center justify-center rounded-[4px] outline-none focus-visible:ring-2 focus-visible:ring-ds-focus">
					{soldOut ? <XIcon className="text-ds-ink-3" /> : <FavoriteHeartIcon active />}
				</button>
			</div>

			{soldOut ?
    // Figma: «Товар закончился» 14/14 w700 #333, под ним в 8px — название 14/14 w400.
    <div className="flex flex-col gap-2 px-1.5">
					<span className="text-[14px] leading-[14px] font-bold tracking-[-0.42px] text-[#333333]">
						{t("wishlist.soldOut", {
          defaultValue: "Товар закончился"
        })}
					</span>
					<span className="line-clamp-1 text-[14px] leading-[14px] tracking-[-0.42px] text-[#1F1F1F]">{title}</span>
				</div> :
    // Figma: цена, название (14/14 w400 #1F1F1F), селект и кнопка — столбцом с шагом 8px.
    <div className="flex flex-col gap-2 px-1.5">
					<Price value={price.value} oldValue={price.oldValue} sale size="sm" />
					<span className="line-clamp-1 text-[14px] leading-[14px] tracking-[-0.42px] text-[#1F1F1F]">{title}</span>

					{/* Figma: «Выбрать размер» — контурная кнопка S (h32 r10, обводка #EAEAEB,
         12/16 w600, шеврон 16); через 8px — серая кнопка S «В корзину».
         Список — DS (белый r10, пункты h39, выбранный #1F1F1F 4 %). */}
					<div className="flex flex-col gap-2">
						{sizes.length > 1 ? <Select value={size} onValueChange={pickSize}>
								<SelectTrigger size="sm" aria-invalid={sizeHint || undefined} className="w-full gap-1.5 border-ds-line-2 bg-transparent px-3 text-[12px] leading-4 font-semibold data-[placeholder]:text-ds-primary [&_svg:not([class*='text-'])]:text-ds-ink-3">
									<SelectValue placeholder={t("wishlist.chooseSize", {
              defaultValue: "Выбрать размер"
            })} />
								</SelectTrigger>
								<SelectContent>
									{sizes.map(s_3 => <SelectItem key={s_3} value={s_3}>
											{s_3}
										</SelectItem>)}
								</SelectContent>
							</Select> : <div className="flex h-8 w-full items-center justify-between rounded-[10px] border border-ds-line-2 px-3 text-[12px] leading-4 font-semibold text-ds-primary">
								<span>{sizes[0] ?? t("wishlist.oneSize", {
              defaultValue: "Один размер"
            })}</span>
								<ChevronDown16Icon className="text-ds-ink-3" />
							</div>}

						<Button variant="gray" size="sm" block onClick={onAdd}>
							{t("product.addToCart")}
						</Button>
						{sizeHint && <p role="alert" className="ds-t4 text-ds-error">
								{t("product.selectSize")}
							</p>}
					</div>
				</div>}
		</div>;
}
function ProductsSkeleton() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex flex-wrap gap-4">{Array.from({
        length: 6
      }, _temp11)}</div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="mt-[26.5px] flex flex-col gap-[26px]">{t0}<div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4 min-[75rem]:grid-cols-6">{Array.from({
          length: 12
        }, _temp12)}</div></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}

// ── Магазины ────────────────────────────────────────────────────────────────
// Два экрана (Figma, A-29):
// * «Избранные магазины» 3871:55166 — три блока подряд: «Ваши любимые магазины»
//   (до 9, с кнопкой «Посмотреть все»), «Магазины похожие на ваши любимые» и
//   «Все магазины» по алфавиту. Кнопка есть только у первого блока.
// * «Посмотреть все» → 3896:113054 — крошки «Все магазины / Ваши любимые
//   магазины» и весь список любимых; «Все магазины» возвращает на первый экран.
function _temp12(__0, i_0) {
  return <div key={i_0} className="flex flex-col gap-2"><Skeleton className="aspect-[173/229] w-full rounded-[4px]" /><div className="flex flex-col gap-2 px-1.5"><Skeleton className="h-4 w-20" /><Skeleton className="h-4 w-24" /><Skeleton className="h-8 w-full rounded-[10px]" /><Skeleton className="mt-0.5 h-8 w-full rounded-[8px]" /></div></div>;
}
function _temp11(_, i) {
  return <Skeleton key={i} className="h-8 w-24 rounded-full" />;
}
type ShopsView = "overview" | "favorites";

/** Сколько любимых магазинов на первом экране: три колонки по три (Figma). */
const FAVORITES_PREVIEW = 9;
function ShopsTab(t0) {
  const $ = _c(52);
  const {
    query
  } = t0;
  const {
    t
  } = useTranslation();
  const shopIds = useWishlist(_temp13);
  const [view, setView] = useState("overview");
  if (query.isPending) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className={cn(TAB_BODY, "grid gap-4 md:grid-cols-2 xl:grid-cols-3")}>{Array.from({
          length: 3
        }, _temp14)}</div>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (query.error) {
    let t1;
    if ($[1] !== query) {
      t1 = <div className={TAB_BODY}><ErrorState onRetry={() => query.refetch()} /></div>;
      $[1] = query;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[3] !== query.data || $[4] !== shopIds || $[5] !== t || $[6] !== view) {
    t7 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const all = query.data ?? [];
      let t8;
      if ($[14] !== shopIds) {
        t8 = s_0 => shopIds.includes(s_0.id);
        $[14] = shopIds;
        $[15] = t8;
      } else {
        t8 = $[15];
      }
      const favorites = all.filter(t8);
      let t9;
      if ($[16] !== shopIds) {
        t9 = s_1 => !shopIds.includes(s_1.id);
        $[16] = shopIds;
        $[17] = t9;
      } else {
        t9 = $[17];
      }
      const similar = all.filter(t9).slice(0, 9);
      let t10;
      if ($[18] !== t) {
        t10 = t("wishlist.favoriteShops", {
          defaultValue: "\u0412\u0430\u0448\u0438 \u043B\u044E\u0431\u0438\u043C\u044B\u0435 \u043C\u0430\u0433\u0430\u0437\u0438\u043D\u044B"
        });
        $[18] = t;
        $[19] = t10;
      } else {
        t10 = $[19];
      }
      const favoritesTitle = t10;
      let t11;
      if ($[20] !== t) {
        t11 = t("wishlist.allShops", {
          defaultValue: "\u0412\u0441\u0435 \u043C\u0430\u0433\u0430\u0437\u0438\u043D\u044B"
        });
        $[20] = t;
        $[21] = t11;
      } else {
        t11 = $[21];
      }
      const allTitle = t11;
      if (view === "favorites") {
        let t12;
        if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
          t12 = () => setView("overview");
          $[22] = t12;
        } else {
          t12 = $[22];
        }
        let t13;
        if ($[23] !== allTitle) {
          t13 = <button type="button" onClick={t12} className="rounded-[4px] text-black/50 outline-none transition-colors hover:text-black/70 focus-visible:ring-2 focus-visible:ring-ds-focus">{allTitle}</button>;
          $[23] = allTitle;
          $[24] = t13;
        } else {
          t13 = $[24];
        }
        let t14;
        if ($[25] !== favoritesTitle) {
          t14 = <span aria-current="page" className="text-[#1F1F1F]">{favoritesTitle}</span>;
          $[25] = favoritesTitle;
          $[26] = t14;
        } else {
          t14 = $[26];
        }
        let t15;
        if ($[27] !== favoritesTitle || $[28] !== t13 || $[29] !== t14) {
          t15 = <nav aria-label={favoritesTitle} className="mt-[26.5px] flex flex-wrap items-center gap-3 text-[14px] leading-[18px]">{t13}{t14}</nav>;
          $[27] = favoritesTitle;
          $[28] = t13;
          $[29] = t14;
          $[30] = t15;
        } else {
          t15 = $[30];
        }
        const t16 = favorites.length === 0 ? <FavoriteShopsEmpty /> : <ShopCards shops={favorites} showBell={true} />;
        let t17;
        if ($[31] !== t16) {
          t17 = <div className="mt-[26px]"><ShopPanel>{t16}</ShopPanel></div>;
          $[31] = t16;
          $[32] = t17;
        } else {
          t17 = $[32];
        }
        let t18;
        if ($[33] !== t15 || $[34] !== t17) {
          t18 = <div className="flex flex-col">{t15}{t17}</div>;
          $[33] = t15;
          $[34] = t17;
          $[35] = t18;
        } else {
          t18 = $[35];
        }
        t7 = t18;
        break bb0;
      }
      const groups = new Map();
      for (const s_2 of [...all].sort(_temp15)) {
        const letter = (s_2.name.trim()[0] ?? "#").toUpperCase();
        const bucket = groups.get(letter);
        if (bucket) {
          bucket.push(s_2);
        } else {
          groups.set(letter, [s_2]);
        }
      }
      if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = cn(TAB_BODY, "flex flex-col gap-5");
        $[36] = t2;
      } else {
        t2 = $[36];
      }
      const t12 = favorites.length > 0 && <Button variant="gray" size="sm" onClick={() => setView("favorites")}>{t("wishlist.viewAllShops", {
          defaultValue: "\u041F\u043E\u0441\u043C\u043E\u0442\u0440\u0435\u0442\u044C \u0432\u0441\u0435"
        })}<ChevronRight16Icon /></Button>;
      const t13 = favorites.length === 0 ? <FavoriteShopsEmpty /> : <ShopCards shops={favorites.slice(0, FAVORITES_PREVIEW)} showBell={true} />;
      if ($[37] !== favoritesTitle || $[38] !== t12 || $[39] !== t13) {
        t3 = <ShopPanel title={favoritesTitle} action={t12}>{t13}</ShopPanel>;
        $[37] = favoritesTitle;
        $[38] = t12;
        $[39] = t13;
        $[40] = t3;
      } else {
        t3 = $[40];
      }
      t4 = similar.length > 0 && <ShopPanel title={t("wishlist.similarShops", {
        defaultValue: "\u041C\u0430\u0433\u0430\u0437\u0438\u043D\u044B \u043F\u043E\u0445\u043E\u0436\u0438\u0435 \u043D\u0430 \u0432\u0430\u0448\u0438 \u043B\u044E\u0431\u0438\u043C\u044B\u0435"
      })}><ShopCards shops={similar} /></ShopPanel>;
      if ($[41] !== allTitle) {
        t5 = <h2 className="text-[20px] leading-[23px] font-semibold tracking-[-0.31px] text-black">{allTitle}</h2>;
        $[41] = allTitle;
        $[42] = t5;
      } else {
        t5 = $[42];
      }
      t1 = "flex flex-col gap-4";
      t6 = [...groups.entries()].map(_temp16);
    }
    $[3] = query.data;
    $[4] = shopIds;
    $[5] = t;
    $[6] = view;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t1 = $[7];
    t2 = $[8];
    t3 = $[9];
    t4 = $[10];
    t5 = $[11];
    t6 = $[12];
    t7 = $[13];
  }
  if (t7 !== Symbol.for("react.early_return_sentinel")) {
    return t7;
  }
  let t8;
  if ($[43] !== t1 || $[44] !== t6) {
    t8 = <div className={t1}>{t6}</div>;
    $[43] = t1;
    $[44] = t6;
    $[45] = t8;
  } else {
    t8 = $[45];
  }
  let t9;
  if ($[46] !== t2 || $[47] !== t3 || $[48] !== t4 || $[49] !== t5 || $[50] !== t8) {
    t9 = <div className={t2}>{t3}{t4}{t5}{t8}</div>;
    $[46] = t2;
    $[47] = t3;
    $[48] = t4;
    $[49] = t5;
    $[50] = t8;
    $[51] = t9;
  } else {
    t9 = $[51];
  }
  return t9;
}
function _temp16(t0) {
  const [letter_0, list] = t0;
  return <ShopPanel key={letter_0} title={letter_0}><ShopCards shops={list} /></ShopPanel>;
}
function _temp15(a, b) {
  return a.name.localeCompare(b.name, "ru");
}
function _temp14(_, i) {
  return <Skeleton key={i} className="h-[190px] rounded-[12px] bg-[#F8F8F8]" />;
}
function _temp13(s) {
  return s.shopIds;
}
function FavoriteShopsEmpty() {
  const $ = _c(7);
  const {
    t
  } = useTranslation();
  let t0;
  if ($[0] !== t) {
    t0 = t("wishlist.emptyShops");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t) {
    t1 = t("wishlist.emptyShopsHint");
    $[2] = t;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t0 || $[5] !== t1) {
    t2 = <div className="rounded-[12px] bg-white"><EmptyState icon={Store} title={t0} hint={t1} /></div>;
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}

// Figma: серая плашка r16 #F8F8F8, поля 16px сверху/снизу и 12px по бокам;
// заголовок 16/20 w600 #000 (и кнопка) — строка над карточками с отступом 16px.
function ShopPanel(t0) {
  const $ = _c(6);
  const {
    title,
    action,
    children
  } = t0;
  let t1;
  if ($[0] !== action || $[1] !== title) {
    t1 = (title || action) && <div className={cn("mb-4 flex items-center justify-between gap-3", action && "min-h-8")}>{title && <h3 className="text-[16px] leading-5 font-semibold tracking-[-0.31px] text-black">{title}</h3>}{action}</div>;
    $[0] = action;
    $[1] = title;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== children || $[4] !== t1) {
    t2 = <section className="rounded-[16px] bg-[#F8F8F8] px-3 py-4">{t1}{children}</section>;
    $[3] = children;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}

// Figma: ряды магазинов собраны в белые карточки r12 (до трёх в ряд, зазор
// 16px), внутри — поля 10px и разделители 1px #F8F8F8 между рядами.
// Раскладываем список по колонкам почти поровну, сохраняя порядок.
function ShopCards(t0) {
  const $ = _c(6);
  const {
    shops,
    showBell: t1
  } = t0;
  const showBell = t1 === undefined ? false : t1;
  const columns = Math.min(3, shops.length);
  const base = Math.floor(shops.length / columns);
  const extra = shops.length % columns;
  let t2;
  if ($[0] !== base || $[1] !== columns || $[2] !== extra || $[3] !== shops || $[4] !== showBell) {
    const chunks = [];
    let from = 0;
    for (let i = 0; i < columns; i++) {
      const size = base + (i < extra ? 1 : 0);
      chunks.push(shops.slice(from, from + size));
      from = from + size;
    }
    t2 = <div className="grid grid-cols-[minmax(0,1fr)] gap-4 md:grid-cols-2 lg:grid-cols-3">{chunks.map(chunk => <div key={chunk[0]?.id} className="flex min-w-0 flex-col divide-y divide-[#F8F8F8] rounded-[12px] bg-white p-2.5">{chunk.map(shop => <ShopRow key={shop.id} shop={shop} showBell={showBell} />)}</div>)}</div>;
    $[0] = base;
    $[1] = columns;
    $[2] = extra;
    $[3] = shops;
    $[4] = showBell;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}

// Ряд магазина: аватар + название + категория (tagline — ближайшие доступные
// данные) + колокольчик (только визуальный локальный тумблер — серверного API
// подписки нет) + сердечко (лайк магазина через стор + сервер).
function ShopRow(t0) {
  const $ = _c(45);
  const {
    shop,
    showBell: t1
  } = t0;
  const showBell = t1 === undefined ? false : t1;
  const {
    t
  } = useTranslation();
  let t2;
  if ($[0] !== shop.id) {
    t2 = s => s.shopIds.includes(shop.id);
    $[0] = shop.id;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const saved = useWishlist(t2);
  const toggleShop = useWishlist(_temp17);
  const setShopLikedOnServer = useWishlist(_temp18);
  const [notify, setNotify] = useState(true);
  let t3;
  if ($[2] !== setShopLikedOnServer || $[3] !== shop.id || $[4] !== toggleShop) {
    t3 = () => {
      const liked = toggleShop(shop.id);
      setShopLikedOnServer(shop.id, liked);
    };
    $[2] = setShopLikedOnServer;
    $[3] = shop.id;
    $[4] = toggleShop;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const onHeart = t3;
  const t4 = `/shop/${shop.slug}`;
  let t5;
  if ($[6] !== shop.logoUrl || $[7] !== shop.name) {
    t5 = shop.logoUrl && <AvatarImage src={shop.logoUrl} alt={shop.name} className="object-cover" />;
    $[6] = shop.logoUrl;
    $[7] = shop.name;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== shop.name) {
    t6 = shop.name.trim().charAt(0);
    $[9] = shop.name;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t6) {
    t7 = <AvatarFallback className="bg-[#D9D9D9] text-[14px] font-semibold text-black/60">{t6}</AvatarFallback>;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t5 || $[14] !== t7) {
    t8 = <Avatar size="lg" className="bg-[#D9D9D9]">{t5}{t7}</Avatar>;
    $[13] = t5;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== shop.name) {
    t9 = <p className="truncate text-[14px] leading-[18px] text-black">{shop.name}</p>;
    $[16] = shop.name;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== shop.tagline) {
    t10 = <p className="truncate text-[12px] leading-4 text-black/50">{shop.tagline}</p>;
    $[18] = shop.tagline;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t10 || $[21] !== t9) {
    t11 = <div className="min-w-0">{t9}{t10}</div>;
    $[20] = t10;
    $[21] = t9;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t11 || $[24] !== t4 || $[25] !== t8) {
    t12 = <Link to={t4} className="flex min-w-0 flex-1 items-center gap-2.5 rounded-[8px] outline-none focus-visible:ring-2 focus-visible:ring-ds-focus">{t8}{t11}</Link>;
    $[23] = t11;
    $[24] = t4;
    $[25] = t8;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== notify || $[28] !== showBell || $[29] !== t) {
    t13 = showBell && <button type="button" aria-pressed={notify} aria-label={notify ? t("wishlist.notifyOff", {
      defaultValue: "\u041E\u0442\u043A\u043B\u044E\u0447\u0438\u0442\u044C \u0443\u0432\u0435\u0434\u043E\u043C\u043B\u0435\u043D\u0438\u044F"
    }) : t("wishlist.notifyOn", {
      defaultValue: "\u0412\u043A\u043B\u044E\u0447\u0438\u0442\u044C \u0443\u0432\u0435\u0434\u043E\u043C\u043B\u0435\u043D\u0438\u044F"
    })} onClick={() => setNotify(_temp19)} className="flex size-6 shrink-0 items-center justify-center rounded-[6px] text-black outline-none focus-visible:ring-2 focus-visible:ring-ds-focus">{notify ? <BellIcon /> : <BellOffIcon />}</button>;
    $[27] = notify;
    $[28] = showBell;
    $[29] = t;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== saved || $[32] !== t) {
    t14 = saved ? t("wishlist.removeShop") : t("wishlist.addShop");
    $[31] = saved;
    $[32] = t;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== saved) {
    t15 = <FavoriteHeartIcon variant="page" active={saved} />;
    $[34] = saved;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== onHeart || $[37] !== saved || $[38] !== t14 || $[39] !== t15) {
    t16 = <button type="button" aria-pressed={saved} aria-label={t14} onClick={onHeart} className="flex size-8 shrink-0 items-center justify-center rounded-[8px] outline-none focus-visible:ring-2 focus-visible:ring-ds-focus">{t15}</button>;
    $[36] = onHeart;
    $[37] = saved;
    $[38] = t14;
    $[39] = t15;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== t12 || $[42] !== t13 || $[43] !== t16) {
    t17 = <div className="flex items-center gap-2.5 py-2">{t12}{t13}{t16}</div>;
    $[41] = t12;
    $[42] = t13;
    $[43] = t16;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  return t17;
}

// ── Идеи ──────────────────────────────────────────────────────────────────────
// Показываем ТОЛЬКО сохранённые идеи: подборки ленты, чья ссылка есть в ideaIds
// вишлиста (тот же контур лайков, что у товаров/магазинов). Карточка — общий
// `IdeaCard` из ленты: обложка, сердечко (залито при лайке) и бейджи.
// Клик по карточке открывает модалку «Идею в корзину» (кадр «Идею в
// корзину.svg») вместо перехода на страницу идеи.
function _temp19(n) {
  return !n;
}
function _temp18(s_1) {
  return s_1.setShopLikedOnServer;
}
function _temp17(s_0) {
  return s_0.toggleShop;
}
function IdeasTab(t0) {
  const $ = _c(28);
  const {
    query
  } = t0;
  const {
    t
  } = useTranslation();
  const ideaIds = useWishlist(_temp20);
  const [openIdea, setOpenIdea] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  if (query.isPending) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <IdeaWall className={TAB_BODY}>{["a", "b", "c", "d", "e", "f", "g", "h"].map(_temp21)}</IdeaWall>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (query.error) {
    let t1;
    if ($[1] !== query) {
      t1 = <div className={TAB_BODY}><ErrorState onRetry={() => query.refetch()} /></div>;
      $[1] = query;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  let T0;
  let t1;
  let t2;
  let t3;
  if ($[3] !== ideaIds || $[4] !== query.data || $[5] !== t) {
    t3 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const liked = ideaIds.length > 0 ? (query.data ?? []).filter(c => ideaIds.includes(ideaLikeId(c))) : [];
      if (liked.length === 0) {
        let t4;
        if ($[10] !== t) {
          t4 = t("wishlist.emptyIdeas", {
            defaultValue: "\u041D\u0435\u0442 \u0441\u043E\u0445\u0440\u0430\u043D\u0451\u043D\u043D\u044B\u0445 \u0438\u0434\u0435\u0439"
          });
          $[10] = t;
          $[11] = t4;
        } else {
          t4 = $[11];
        }
        let t5;
        if ($[12] !== t) {
          t5 = t("wishlist.emptyIdeasHint", {
            defaultValue: "\u041D\u0430\u0436\u0438\u043C\u0430\u0439\u0442\u0435 \u043D\u0430 \u0441\u0435\u0440\u0434\u0435\u0447\u043A\u043E \u0432 \u043B\u0435\u043D\u0442\u0435 \u0438\u0434\u0435\u0439, \u0447\u0442\u043E\u0431\u044B \u0441\u043E\u0445\u0440\u0430\u043D\u0438\u0442\u044C \u0432\u0434\u043E\u0445\u043D\u043E\u0432\u0435\u043D\u0438\u0435 \u0441\u044E\u0434\u0430."
          });
          $[12] = t;
          $[13] = t5;
        } else {
          t5 = $[13];
        }
        let t6;
        if ($[14] !== t4 || $[15] !== t5) {
          t6 = <div className={TAB_BODY}><EmptyState icon={Sparkles} title={t4} hint={t5} /></div>;
          $[14] = t4;
          $[15] = t5;
          $[16] = t6;
        } else {
          t6 = $[16];
        }
        t3 = t6;
        break bb0;
      }
      T0 = IdeaWall;
      t1 = TAB_BODY;
      let t4;
      if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = c_0 => <IdeaCard key={c_0.id} collection={c_0} onOpen={idea => {
          setOpenIdea(idea);
          setModalOpen(true);
        }} />;
        $[17] = t4;
      } else {
        t4 = $[17];
      }
      t2 = liked.map(t4);
    }
    $[3] = ideaIds;
    $[4] = query.data;
    $[5] = t;
    $[6] = T0;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    T0 = $[6];
    t1 = $[7];
    t2 = $[8];
    t3 = $[9];
  }
  if (t3 !== Symbol.for("react.early_return_sentinel")) {
    return t3;
  }
  let t4;
  if ($[18] !== T0 || $[19] !== t1 || $[20] !== t2) {
    t4 = <T0 className={t1}>{t2}</T0>;
    $[18] = T0;
    $[19] = t1;
    $[20] = t2;
    $[21] = t4;
  } else {
    t4 = $[21];
  }
  let t5;
  if ($[22] !== modalOpen || $[23] !== openIdea) {
    t5 = <IdeaCartModal collection={openIdea} open={modalOpen} onOpenChange={setModalOpen} />;
    $[22] = modalOpen;
    $[23] = openIdea;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  let t6;
  if ($[25] !== t4 || $[26] !== t5) {
    t6 = <>{t4}{t5}</>;
    $[25] = t4;
    $[26] = t5;
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  return t6;
}
function _temp21(key) {
  return <Skeleton key={key} className="size-full rounded-[4px]" />;
}
function _temp20(s) {
  return s.ideaIds;
}